import { useEffect, useCallback } from 'react';
import { useFormContext } from '@atlassian/jira-issue-create-form-context';
import { useTriggerIssueCreateModal } from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal';

type UseShouldShowConfirmDismissAiSuggestedModalReturnType = [
	{
		shouldShowConfirmDismissFromGlobalCreate: boolean;
		shouldShowConfirmDismissFromCurrentlyEditedIssue: boolean;
	},
	{
		handleNextModalState: () => void;
	},
];

export const useShouldShowConfirmDismissAiSuggestedModal =
	(): UseShouldShowConfirmDismissAiSuggestedModalReturnType => {
		const [currentState, { resetWasModalRetriggered, setMinimizableModalView, restoreState }] =
			useTriggerIssueCreateModal();
		const [{ selectedProject, isFormDirty }] = useFormContext();
		const { wasModalRetriggered, minimizableModalView, openIssueCreateModalNextState } =
			currentState;

		// Conditions for AI Suggested Discard modal visibility
		const isAiEnabledAndRetriggered = minimizableModalView === 'ai-enabled' && wasModalRetriggered;
		const isGlobalCreateTriggered =
			openIssueCreateModalNextState &&
			openIssueCreateModalNextState.triggerPointKey === 'global-create-button';

		const shouldShowConfirmDismissFromGlobalCreate =
			isAiEnabledAndRetriggered && isGlobalCreateTriggered;
		const shouldShowConfirmDismissFromCurrentlyEditedIssue =
			isAiEnabledAndRetriggered && isFormDirty && !!selectedProject;

		// Interupt the Global Issue Create dialog flow, that re-triggers form
		// fields config fetch.
		useEffect(() => {
			if (shouldShowConfirmDismissFromGlobalCreate) {
				restoreState({
					...currentState,
					openIssueCreateModalNextState: undefined,
				});
			}
		}, [shouldShowConfirmDismissFromGlobalCreate, currentState, restoreState]);

		const handleNextModalState = useCallback(() => {
			// Make sure the view doesn't flip to full modal while the confirmation
			// modal is displayed.
			resetWasModalRetriggered(false);
			setMinimizableModalView('ai-enabled');
		}, [resetWasModalRetriggered, setMinimizableModalView]);

		return [
			{
				shouldShowConfirmDismissFromGlobalCreate,
				shouldShowConfirmDismissFromCurrentlyEditedIssue,
			},
			{ handleNextModalState },
		];
	};
